import { Stack } from "@mui/material";
import { ApplicationSelect } from "../../../shared/components/application-select";
import { usePortfolioSelection } from "../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { ChangingDirectionType } from "../types/ChangingDirectionType";
import { SelectorButtonContent } from "../views/SelectorButtonContent";
import { ToolsPanelButton } from "./ToolsPanelButton";

type StepChangeSelectorProps = {
    selectedStep: number;
    setSelectedStep: React.Dispatch<React.SetStateAction<number>>;
    stepOptions: number[];
    disabled: boolean;
    buttonContent: string;
    previousTooltipText: string;
    nextTooltipText: string;
    selectorWidth?: number;
    fontWeight?: number | string;
    fontSize?: number | string;
    onChange: (direction: ChangingDirectionType, step: number) => void | Promise<void>;
    onStepSelectionChange?: (step: number) => void | Promise<void>;
    forceUsePopupForStepChangeSelector?: boolean
};

export function StepChangeSelector({
    selectedStep,
    setSelectedStep,
    stepOptions,
    disabled,
    buttonContent,
    previousTooltipText,
    nextTooltipText,
    selectorWidth = 70,
    fontWeight = "bold",
    fontSize = "15px",
    onChange,
    onStepSelectionChange,
    forceUsePopupForStepChangeSelector = false,
}: StepChangeSelectorProps) {
    const { selectedPortfolio } = usePortfolioSelection();

    const handleChange = (direction: ChangingDirectionType): void => {
        void onChange(direction, selectedStep);
    };

    const handleStepSelection = (value: string): void => {
        const selectedStep = parseFloat(value);
        setSelectedStep(selectedStep);
        void onStepSelectionChange?.(selectedStep);
    };

    const handleContextMenu = (
        event: React.MouseEvent<HTMLButtonElement>,
        offset: number
    ): void => {
        event.preventDefault();
        event.stopPropagation();

        if (stepOptions.length === 0) {
            return;
        }

        let currentIndex = stepOptions.indexOf(selectedStep);
        
        if (currentIndex < 0) {
            currentIndex = offset > 0 ? -1 : 0;
        }

        const nextIndex = (currentIndex + offset + stepOptions.length) % stepOptions.length;
        const nextStep = stepOptions[nextIndex];

        setSelectedStep(nextStep);
        void onStepSelectionChange?.(nextStep);
    };

    const displayPrecision = Math.max(
        ...stepOptions.map((option) => {
            const [, fraction = ''] = option.toString().split('.');
            return fraction.length;
        })
    );

    const formatStepValue = (value: number): string =>
        displayPrecision > 0
            ? value.toFixed(displayPrecision)
            : value.toString();

    const getTooltipText = (baseText: string): string =>
        `${baseText} by ${formatStepValue(selectedStep)}`;

    const usePopupForStepChangeSelector =
        forceUsePopupForStepChangeSelector ||
        (selectedPortfolio?.portfolioSettings?.usePopupForStepChangeSelector ?? false);

    return (
        <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
        >
            {usePopupForStepChangeSelector &&
                <ApplicationSelect
                    value={selectedStep.toString()}
                    options={stepOptions.map((option) => ({
                        value: option.toString(),
                        label: formatStepValue(option),
                    }))}
                    width={selectorWidth}
                    menuWidth={selectorWidth}
                    disabled={disabled}
                    fontWeight={fontWeight}
                    fontSize={fontSize}
                    menuItemJustifyContent="flex-end"
                    menuItemTextAlign="right"
                    renderValue={(value) => formatStepValue(parseFloat(value))}
                    onChange={handleStepSelection}
                />
            }
            <ToolsPanelButton
                tooltipText={
                    <>
                        {getTooltipText(previousTooltipText)}
                        <br/>
                        Right-click to decrease the step size
                    </>
                }
                disabled={disabled}
                onChanged={() => handleChange(ChangingDirectionType.Previous)}
                onContextMenu={(event) => handleContextMenu(event, 1)}
            >
                <SelectorButtonContent content={buttonContent} direction={ChangingDirectionType.Previous} />
            </ToolsPanelButton>
            <ToolsPanelButton
                tooltipText={
                    <>
                        {getTooltipText(nextTooltipText)}
                        <br/>
                        Right-click to increase the step size
                    </>
                }
                disabled={disabled}
                onChanged={() => handleChange(ChangingDirectionType.Next)}
                onContextMenu={(event) => handleContextMenu(event, -1)}
            >
                <SelectorButtonContent content={buttonContent} direction={ChangingDirectionType.Next} />
            </ToolsPanelButton>
        </Stack>
    );
}
